// S12 (v0.12.0) — Entity Browser.
//
// Layout: 320px-wide tree on the LEFT, tabbed detail panel on the
// RIGHT. The selected entity is mirrored to a ``?selected=`` URL param
// so links/refreshes preserve context.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";

import {
  useAttachDevice,
  useAssignRole,
  useCreateEntity,
  useDeleteAttribute,
  useDeleteEntity,
  useDetachDevice,
  useEntities,
  useEntity,
  useEntityAttributes,
  useEntityDevices,
  useEntityHealth,
  useEntityRoles,
  useEntityTree,
  useEntityTrees,
  useEntityTypes,
  useMoveEntity,
  usePutAttribute,
  useRevokeRole,
  type Entity,
} from "@/api/entities";
import { useCameras } from "@/api/cameras";
import { useUsers } from "@/api/users";
import { EntityBreadcrumb } from "@/components/EntityBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useFormatTimestamp } from "@/utils/datetime";

type TabKey = "overview" | "cameras" | "attributes" | "users" | "health";

// S3 — color-coded compliance dot drawn next to each tree node. Uses
// useEntityHealth's 60-second cache so opening a 200-node tree
// triggers 200 queries that all coalesce to 200 unique refetches per
// minute. For very large trees this can be moved server-side later
// via a batched endpoint; for now individual hooks are simplest.
function HealthDot({ entityId }: { entityId: string }) {
  const { data } = useEntityHealth(entityId);
  let tone = "bg-muted-foreground/30"; // grey (no data yet)
  if (data) {
    if (data.compliance_score === null) {
      tone = "bg-muted-foreground/30"; // no cameras
    } else if (data.compliance_score >= 80) {
      tone = "bg-emerald-500";
    } else if (data.compliance_score >= 50) {
      tone = "bg-amber-500";
    } else {
      tone = "bg-red-500";
    }
  }
  return (
    <span
      className={cn("inline-block h-2 w-2 shrink-0 rounded-full", tone)}
      title={
        data?.compliance_score === null
          ? "no cameras"
          : data
            ? `compliance ${data.compliance_score}%`
            : "loading…"
      }
    />
  );
}

function buildChildrenIndex(rows: Entity[] | undefined): Map<string | null, Entity[]> {
  const idx = new Map<string | null, Entity[]>();
  for (const e of rows ?? []) {
    const key = e.parent_id;
    const arr = idx.get(key) ?? [];
    arr.push(e);
    idx.set(key, arr);
  }
  for (const [k, list] of idx) {
    list.sort((a, b) => a.name.localeCompare(b.name));
    idx.set(k, list);
  }
  return idx;
}

// Tiny recursive tree node.
function TreeNode({
  entity,
  childrenIdx,
  depth,
  selectedId,
  expanded,
  toggle,
  onSelect,
}: {
  entity: Entity;
  childrenIdx: Map<string | null, Entity[]>;
  depth: number;
  selectedId: string | null;
  expanded: Set<string>;
  toggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  const kids = childrenIdx.get(entity.id) ?? [];
  const isExpanded = expanded.has(entity.id);
  const isSelected = selectedId === entity.id;
  return (
    <div>
      <button
        onClick={() => onSelect(entity.id)}
        className={cn(
          "flex w-full items-center gap-1 rounded px-2 py-1 text-left text-sm hover:bg-muted",
          isSelected && "bg-[#21313c]/10 font-medium text-primary",
        )}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        {kids.length > 0 ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggle(entity.id);
            }}
            className="inline-flex h-4 w-4 items-center justify-center text-muted-foreground"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-3 w-3" />
            ) : (
              <ChevronRightIcon className="h-3 w-3" />
            )}
          </span>
        ) : (
          <span className="inline-block h-4 w-4" />
        )}
        <HealthDot entityId={entity.id} />
        <span className="truncate">{entity.name}</span>
      </button>
      {isExpanded &&
        kids.map((k) => (
          <TreeNode
            key={k.id}
            entity={k}
            childrenIdx={childrenIdx}
            depth={depth + 1}
            selectedId={selectedId}
            expanded={expanded}
            toggle={toggle}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

export default function EntityBrowserPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("selected");
  const [tab, setTab] = useState<TabKey>("overview");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // A31 — fetch EVERY root's subtree (not just roots[0]), so all root trees expand.
  const { data: roots = [] } = useEntities();
  const trees = useEntityTrees(roots.map((r) => r.id));
  const treeData = trees.map((t) => t.data);
  const allRows: Entity[] = useMemo(() => {
    const set = new Map<string, Entity>();
    for (const r of roots) set.set(r.id, r);
    for (const sub of treeData) for (const e of sub ?? []) set.set(e.id, e);
    return Array.from(set.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, ...treeData]);
  const childrenIdx = useMemo(() => buildChildrenIndex(allRows), [allRows]);

  function selectEntity(id: string) {
    const next = new URLSearchParams(params);
    next.set("selected", id);
    setParams(next, { replace: true });
  }
  function toggle(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Expand the path to the selected entity once its row is known (from any root tree).
  useEffect(() => {
    if (!selectedId) return;
    const byId = new Map(allRows.map((e) => [e.id, e]));
    if (!byId.has(selectedId)) return;
    const path = new Set<string>();
    let cur = byId.get(selectedId);
    while (cur && cur.parent_id) {
      path.add(cur.parent_id);
      cur = byId.get(cur.parent_id);
    }
    setExpanded((prev) => new Set([...prev, ...path]));
  }, [selectedId, allRows]);

  return (
    <div className="flex h-[calc(100vh-72px)] flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Entities</h1>
        <NewEntityButton parentId={selectedId} />
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <Card className="w-[320px] shrink-0 overflow-hidden">
          <div className="border-b border-border bg-primary px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white">
            Hierarchy
          </div>
          <CardContent className="max-h-full overflow-y-auto p-2">
            {roots.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No entities yet. Click <strong>Add Entity</strong>.
              </p>
            ) : (
              <div className="space-y-0">
                {roots.map((r) => (
                  <TreeNode
                    key={r.id}
                    entity={r}
                    childrenIdx={childrenIdx}
                    depth={0}
                    selectedId={selectedId}
                    expanded={expanded}
                    toggle={toggle}
                    onSelect={selectEntity}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {selectedId ? (
            <DetailPanel entityId={selectedId} tab={tab} setTab={setTab} />
          ) : (
            <CardContent className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              Select an entity on the left to view details.
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}

function NewEntityButton({ parentId }: { parentId: string | null }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const { data: types = [] } = useEntityTypes();
  const [typeId, setTypeId] = useState("");
  const create = useCreateEntity();

  useEffect(() => {
    if (!typeId && types[0]) setTypeId(types[0].id);
  }, [types, typeId]);

  async function handleCreate() {
    if (!name.trim() || !typeId) return;
    await create.mutateAsync({
      name: name.trim(),
      entity_type_id: typeId,
      parent_id: parentId,
    });
    setName("");
    setOpen(false);
  }

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
      >
        <PlusIcon className="mr-1 h-4 w-4" />
        Add Entity
      </Button>
    );
  }
  return (
    <Card className="absolute right-6 top-20 z-10 w-80 p-3 shadow-lg">
      <div className="space-y-2">
        <Label>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <Label>Type</Label>
        <select
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
          className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
        >
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Parent: {parentId ? "current selection" : "(root)"}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={create.isPending}>
            Create
          </Button>
        </div>
      </div>
    </Card>
  );
}

function DetailPanel({
  entityId,
  tab,
  setTab,
}: {
  entityId: string;
  tab: TabKey;
  setTab: (t: TabKey) => void;
}) {
  const { data: entity } = useEntity(entityId);
  if (!entity) {
    return (
      <CardContent className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Loading…
      </CardContent>
    );
  }
  return (
    <>
      <div className="border-b border-border bg-primary px-4 py-3 text-white">
        <EntityBreadcrumb
          entityId={entity.id}
          className="mb-1 flex items-center gap-1 text-xs text-white/70"
        />
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{entity.name}</h2>
          <Badge variant={entity.is_active ? "online" : "offline"}>
            {entity.is_active ? "active" : "inactive"}
          </Badge>
        </div>
      </div>
      <div className="border-b border-border bg-muted/30">
        <nav className="flex gap-4 px-4 text-sm">
          {(
            [
              ["overview", "Overview"],
              ["cameras", "Cameras"],
              ["attributes", "Attributes"],
              ["users", "Users & Roles"],
              ["health", "Health"],
            ] as [TabKey, string][]
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn(
                "border-b-2 px-1 py-2 transition-colors",
                tab === k
                  ? "border-[#ff6d00] text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
      <CardContent className="flex-1 overflow-y-auto p-4">
        {tab === "overview" && <OverviewTab entityId={entityId} />}
        {tab === "cameras" && <CamerasTab entityId={entityId} />}
        {tab === "attributes" && <AttributesTab entityId={entityId} />}
        {tab === "users" && <UsersTab entityId={entityId} />}
        {tab === "health" && <HealthTab entityId={entityId} />}
      </CardContent>
    </>
  );
}

function OverviewTab({ entityId }: { entityId: string }) {
  const { data: entity } = useEntity(entityId);
  const { data: subtree } = useEntityTree(entityId);
  const { data: roots = [] } = useEntities();
  const move = useMoveEntity();
  const del = useDeleteEntity();
  const fmt = useFormatTimestamp();
  const [pendingParent, setPendingParent] = useState<string | null>(null);
  const [confirmMove, setConfirmMove] = useState(false);
  if (!entity) return null;
  const flatRows: Entity[] = useMemoFlatten(roots, subtree);
  const candidateParents = flatRows.filter(
    (e) => e.id !== entity.id && !isDescendant(flatRows, entity.id, e.id),
  );
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
        <dt className="text-muted-foreground">ID</dt>
        <dd className="font-mono text-xs">{entity.id}</dd>
        <dt className="text-muted-foreground">Type ID</dt>
        <dd className="font-mono text-xs">{entity.entity_type_id}</dd>
        <dt className="text-muted-foreground">Created</dt>
        <dd>{fmt(entity.created_at)}</dd>
        <dt className="text-muted-foreground">Descendants</dt>
        <dd>{(subtree?.length ?? 1) - 1}</dd>
      </dl>
      <div className="space-y-1 border-t border-border pt-3">
        <Label>Move under</Label>
        <select
          value={entity.parent_id ?? ""}
          onChange={(e) => {
            const next = e.target.value || null;
            if (next !== (entity.parent_id ?? null)) {
              setPendingParent(next);
              setConfirmMove(true);
            }
          }}
          className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
        >
          <option value="">(root)</option>
          {candidateParents.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <ConfirmDialog
        open={confirmMove}
        onOpenChange={setConfirmMove}
        tone="default"
        title="Move entity?"
        description={
          <>
            Move <span className="font-semibold">{entity.name}</span> under{" "}
            <span className="font-semibold">
              {pendingParent
                ? candidateParents.find((c) => c.id === pendingParent)?.name ?? "the selected parent"
                : "(root)"}
            </span>
            . Its descendants move with it.
          </>
        }
        confirmLabel="Move entity"
        busy={move.isPending}
        onConfirm={() =>
          move.mutate(
            { id: entity.id, new_parent_id: pendingParent },
            { onSuccess: () => setConfirmMove(false) },
          )
        }
      />
      {/* del confirm is native window.confirm below; reparent is the ConfirmDialog above */}
      <Button
        variant="destructive"
        onClick={() => {
          if (
            confirm(
              `Delete ${entity.name} and all its descendants? Cameras stay; only entity bindings are removed.`,
            )
          ) {
            del.mutate(entity.id);
          }
        }}
      >
        <TrashIcon className="mr-1 h-4 w-4" />
        Delete entity
      </Button>
    </div>
  );
}

function useMemoFlatten(roots: Entity[], subtree: Entity[] | undefined): Entity[] {
  return useMemo(() => {
    const set = new Map<string, Entity>();
    for (const r of roots) set.set(r.id, r);
    for (const r of subtree ?? []) set.set(r.id, r);
    return Array.from(set.values());
  }, [roots, subtree]);
}

function isDescendant(rows: Entity[], rootId: string, candidate: string): boolean {
  const idx = new Map<string | null, Entity[]>();
  for (const e of rows) {
    const arr = idx.get(e.parent_id) ?? [];
    arr.push(e);
    idx.set(e.parent_id, arr);
  }
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === candidate) return true;
    for (const c of idx.get(cur) ?? []) queue.push(c.id);
  }
  return false;
}

function CamerasTab({ entityId }: { entityId: string }) {
  const { data: devices = [] } = useEntityDevices(entityId);
  const { data: camerasList } = useCameras();
  const attach = useAttachDevice(entityId);
  const detach = useDetachDevice(entityId);
  const [picker, setPicker] = useState("");

  const attached = new Set(
    devices.filter((d) => d.device_type === "camera").map((d) => d.device_id),
  );
  const camById = new Map(
    (camerasList?.items ?? []).map((c) => [c.id, c]),
  );
  const unbound = (camerasList?.items ?? []).filter((c) => !attached.has(c.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <select
          value={picker}
          onChange={(e) => setPicker(e.target.value)}
          className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm"
        >
          <option value="">Choose camera to attach…</option>
          {unbound.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <Button
          disabled={!picker}
          onClick={() => {
            attach.mutate({ device_type: "camera", device_id: picker });
            setPicker("");
          }}
        >
          Attach
        </Button>
      </div>
      <ul className="divide-y divide-border rounded border border-border">
        {devices
          .filter((d) => d.device_type === "camera")
          .map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span>{camById.get(d.device_id)?.name ?? d.device_id}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  detach.mutate({
                    device_type: "camera",
                    device_id: d.device_id,
                  })
                }
              >
                <TrashIcon className="h-3 w-3" />
              </Button>
            </li>
          ))}
        {devices.filter((d) => d.device_type === "camera").length === 0 && (
          <li className="px-3 py-3 text-sm text-muted-foreground">
            No cameras attached.
          </li>
        )}
      </ul>
    </div>
  );
}

function AttributesTab({ entityId }: { entityId: string }) {
  const { data: attrs = [] } = useEntityAttributes(entityId);
  const put = usePutAttribute(entityId);
  const del = useDeleteAttribute(entityId);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label>Key</Label>
          <Input value={key} onChange={(e) => setKey(e.target.value)} />
        </div>
        <div className="flex-1">
          <Label>Value</Label>
          <Input value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <Button
          disabled={!key}
          onClick={() => {
            put.mutate({ key, value });
            setKey("");
            setValue("");
          }}
        >
          Save
        </Button>
      </div>
      <ul className="divide-y divide-border rounded border border-border">
        {attrs.map((a) => (
          <li
            key={a.key}
            className="flex items-center justify-between px-3 py-2 text-sm"
          >
            <span>
              <span className="text-muted-foreground">{a.key}</span> ·{" "}
              <span>{JSON.stringify(a.value)}</span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => del.mutate(a.key)}
            >
              <TrashIcon className="h-3 w-3" />
            </Button>
          </li>
        ))}
        {attrs.length === 0 && (
          <li className="px-3 py-3 text-sm text-muted-foreground">
            No attributes.
          </li>
        )}
      </ul>
    </div>
  );
}

function UsersTab({ entityId }: { entityId: string }) {
  const { data: roles = [] } = useEntityRoles(entityId);
  const assign = useAssignRole(entityId);
  const revoke = useRevokeRole(entityId);
  const { data: users } = useUsers();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("viewer");
  const userById = new Map((users?.items ?? []).map((u) => [u.id, u]));
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label>User</Label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="">Choose user…</option>
            {(users?.items ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </select>
        </div>
        <div className="w-32">
          <Label>Role</Label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="viewer">viewer</option>
            <option value="operator">operator</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <Button
          disabled={!userId}
          onClick={() => {
            assign.mutate({ user_id: userId, role });
            setUserId("");
          }}
        >
          Assign
        </Button>
      </div>
      <ul className="divide-y divide-border rounded border border-border">
        {roles.map((r) => (
          <li
            key={`${r.user_id}-${r.role}`}
            className="flex items-center justify-between px-3 py-2 text-sm"
          >
            <span>
              <strong>{userById.get(r.user_id)?.email ?? r.user_id}</strong>
              <span className="ml-2 text-muted-foreground">· {r.role}</span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                revoke.mutate({ user_id: r.user_id, role: r.role })
              }
            >
              <TrashIcon className="h-3 w-3" />
            </Button>
          </li>
        ))}
        {roles.length === 0 && (
          <li className="px-3 py-3 text-sm text-muted-foreground">
            No role assignments.
          </li>
        )}
      </ul>
    </div>
  );
}

function HealthTab({ entityId }: { entityId: string }) {
  const { data: health, refetch, isFetching } = useEntityHealth(entityId);
  const fmt = useFormatTimestamp();
  if (!health) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const total = health.total_cameras;
  const noCams = total === 0;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          {noCams
            ? "No cameras in this subtree yet."
            : `${health.cameras_online} of ${total} cameras online · ${health.cameras_recording} recording`}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Total" value={total} />
        <Stat label="Online" value={health.cameras_online} tone="emerald" />
        <Stat label="Offline" value={health.cameras_offline} tone="red" />
        <Stat label="Recording" value={health.cameras_recording} tone="amber" />
      </div>

      {!noCams && (
        <div className="space-y-3">
          <Bar
            label="Uptime"
            pct={health.uptime_pct ?? 0}
            tone={pctTone(health.uptime_pct)}
          />
          <Bar
            label="Recording"
            pct={health.recording_pct ?? 0}
            tone={pctTone(health.recording_pct)}
          />
          <Bar
            label="Compliance Score"
            pct={health.compliance_score ?? 0}
            tone={pctTone(health.compliance_score)}
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Last updated {fmt(health.last_updated)}
      </p>
    </div>
  );
}

function pctTone(p: number | null): "emerald" | "amber" | "red" {
  if (p === null || p < 50) return "red";
  if (p < 80) return "amber";
  return "emerald";
}

function Bar({
  label,
  pct,
  tone,
}: {
  label: string;
  pct: number;
  tone: "emerald" | "amber" | "red";
}) {
  const bg =
    tone === "emerald"
      ? "bg-emerald-500"
      : tone === "amber"
        ? "bg-amber-500"
        : "bg-red-500";
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">{clamped.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-2 rounded-full", bg)}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "emerald" | "red" | "amber";
}) {
  const colour =
    tone === "emerald"
      ? "text-emerald-600"
      : tone === "red"
        ? "text-red-600"
        : tone === "amber"
          ? "text-amber-600"
          : "text-foreground";
  return (
    <div className="rounded border border-border bg-muted/20 p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={cn("text-2xl font-semibold", colour)}>{value}</p>
    </div>
  );
}
